import React, { useRef, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Edit, Upload } from "@mui/icons-material";
import * as XLSX from "xlsx";
import {
  ComsCatalogItem,
  ComsWorkDoneItem,
  useGetComsCatalogQuery,
  useGetComsBlockByIdQuery,
  useGetComsWorkDoneQuery,
  useUpdateComsCatalogItemMutation,
  useUploadBaselineActualsMutation,
} from "../../redux/comsApi";
import { BILLS, SECTION_TO_BILL } from "./boq-constants";

const COMS_NAVY = "#123456";
const fmt = (v: number) =>
  "$" +
  (v ?? 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
const fmtQty = (v: number) =>
  (v ?? 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

// ─── Enriched item adds computed fields ───────────────────────────────────────
interface EnrichedItem extends ComsCatalogItem {
  dailyQty: number;
  actualQty: number;
  plannedAmt: number;
  actualAmt: number;
  progress: number;
  boqEntryId?: string; // id of per-block BOQ entry (shared-catalogue path only)
  sharedId?: string;   // always the shared catalogue item's own ID (shared-catalogue path only)
}

interface Section {
  bill: number | null;
  billDesc: string;
  items: EnrichedItem[];
  plannedTotal: number;
  actualTotal: number;
  bill12ProfitTotal: number;
  bill12AttendanceTotal: number;
  sectionProgress: number;
}

// ─── Component ────────────────────────────────────────────────────────────────
interface Props {
  blockId: string;
}

const effectivePlannedQty = (
  bill: number | null,
  unit: string | undefined,
  plannedQty: number,
  rate: number,
) => {
  // Bill 12 has lump-sum provisional lines where qty may be imported as 0.
  // Treat these as one item so planned totals include the contractual sum.
  if (
    bill === 12 &&
    plannedQty === 0 &&
    rate > 0 &&
    (unit ?? "").trim().toLowerCase() === "item"
  ) {
    return 1;
  }
  return plannedQty;
};

const WorkDone: React.FC<Props> = ({ blockId }) => {
  // ── Block (for projectType) ──────────────────────────────────────────────
  const { data: blockData } = useGetComsBlockByIdQuery(blockId);
  const catalogueType = blockData?.projectType as "type1" | "type2" | undefined;

  // ── Shared catalogue (item definitions) ─────────────────────────────────
  const { data: sharedCatalog = [], isLoading: loadingShared } =
    useGetComsCatalogQuery({ catalogueType }, { skip: !catalogueType });

  // ── Per-block BOQ entries (plannedQty, openingActualQty) ─────────────────
  // These are per-block activity items that link back to shared catalogue items
  const { data: boqEntries = [], isLoading: loadingBoq } =
    useGetComsCatalogQuery(
      { blockId, type: "activity" },
      { skip: !catalogueType }, // only needed for shared-catalogue flow
    );

  // ── Legacy catalog (blocks without projectType) ──────────────────────────
  const { data: legacyCatalog = [], isLoading: loadingLegacy } =
    useGetComsCatalogQuery(
      { blockId, type: "activity" },
      { skip: !!catalogueType },
    );

  const { data: workDone = [], isLoading: loadingWD } =
    useGetComsWorkDoneQuery(blockId);
  const [updateItem] = useUpdateComsCatalogItemMutation();
  const [uploadBaseline] = useUploadBaselineActualsMutation();

  // ── Edit item dialog state ─────────────────────────────────────────────
  const [editItem, setEditItem] = useState<EnrichedItem | null>(null);
  const [editName, setEditName] = useState("");
  const [editPlannedQty, setEditPlannedQty] = useState("");
  const [editOpeningQty, setEditOpeningQty] = useState("");
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState("");

  // ── Baseline upload dialog state ─────────────────────────────────────────
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadItems, setUploadItems] = useState<any[]>([]);
  const [uploadFileName, setUploadFileName] = useState("");
  const [uploadError, setUploadError] = useState("");
  const [uploadResult, setUploadResult] = useState<{
    matched: number;
    unmatched: any[];
    overwritten: number;
  } | null>(null);
  const [uploading, setUploading] = useState(false);
  const [needsOverwrite, setNeedsOverwrite] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  // Build lookup: catalogItemId → totalQuantity
  const dailyQtyMap = useMemo(() => {
    const m = new Map<string, number>();
    (workDone as ComsWorkDoneItem[]).forEach((item) => {
      if (item.catalogItemId) {
        m.set(
          item.catalogItemId,
          (m.get(item.catalogItemId) ?? 0) + item.totalQuantity,
        );
      } else {
        m.set(item.name, (m.get(item.name) ?? 0) + item.totalQuantity);
      }
    });
    return m;
  }, [workDone]);

  // ── Build sections (shared-catalogue path or legacy path) ─────────────────
  const { sections, grandPlanned, grandActual, grandProgress, catalog } =
    useMemo(() => {
      // ── Shared-catalogue path ────────────────────────────────────────────
      if (catalogueType && sharedCatalog.length > 0) {
        // Index per-block BOQ entries by sharedItemId for fast lookup
        const boqBySharedId = new Map<string, ComsCatalogItem>();
        for (const e of boqEntries as ComsCatalogItem[]) {
          if (e.sharedItemId) boqBySharedId.set(e.sharedItemId, e);
        }

        const sorted = [...(sharedCatalog as ComsCatalogItem[])].sort(
          (a, b) => {
            const ba = a.billNumber ?? 999;
            const bb = b.billNumber ?? 999;
            if (ba !== bb) return ba - bb;
            return (a.displayOrder ?? 0) - (b.displayOrder ?? 0);
          },
        );

        const sectionMap = new Map<number | null, Section>();

        for (const shared of sorted) {
          const bill = shared.billNumber ?? null;
          if (!sectionMap.has(bill)) {
            const billEntry =
              bill != null ? BILLS.find((b) => b.number === bill) : null;
            sectionMap.set(bill, {
              bill,
              billDesc:
                billEntry?.description ??
                (bill ? `Bill ${bill}` : "Unassigned"),
              items: [],
              plannedTotal: 0,
              actualTotal: 0,
              bill12ProfitTotal: 0,
              bill12AttendanceTotal: 0,
              sectionProgress: 0,
            });
          }

          const boqEntry = shared.id ? boqBySharedId.get(shared.id) : undefined;
          const openingQty = boqEntry?.openingActualQty ?? 0;
          // Daily qty is keyed by the SHARED item's ID (daily report entries reference shared catalogue)
          const dailyQty =
            (shared.id ? dailyQtyMap.get(shared.id) : undefined) ??
            dailyQtyMap.get(shared.name) ??
            0;
          const actualQty = openingQty + dailyQty;
          const plannedQty = effectivePlannedQty(
            bill,
            shared.unit,
            boqEntry?.plannedQty ?? 0,
            shared.defaultRate ?? 0,
          );
          const rate = shared.defaultRate ?? 0;
          const plannedAmt = plannedQty * rate;
          const actualAmt = actualQty * rate;
          const progress = plannedQty > 0 ? (actualQty / plannedQty) * 100 : 0;

          // Merge: use shared item definition but expose boqEntry fields for mark-complete
          const merged: EnrichedItem = {
            ...shared,
            // Overlay per-block fields from BOQ entry
            id: boqEntry?.id ?? shared.id, // use boqEntry id for mark-complete mutations
            plannedQty,
            openingActualQty: openingQty,
            markComplete: boqEntry?.markComplete ?? false,
            markCompleteAdjQty: boqEntry?.markCompleteAdjQty ?? 0,
            // Computed
            dailyQty,
            actualQty,
            plannedAmt,
            actualAmt,
            progress,
            boqEntryId: boqEntry?.id,
            sharedId: shared.id, // stable reference to the shared catalogue item ID
          };

          const sec = sectionMap.get(bill)!;
          sec.items.push(merged);
          sec.plannedTotal += plannedAmt;
          sec.actualTotal += actualAmt;
          if (bill === 12) {
            sec.bill12ProfitTotal += plannedAmt * 0.05;
            sec.bill12AttendanceTotal += plannedAmt * 0.05;
          }
        }

        const sections: Section[] = [];
        let grandPlannedBase = 0,
          grandActualBase = 0,
          grandPlanned = 0,
          grandActual = 0;
        for (const sec of sectionMap.values()) {
          sec.sectionProgress =
            sec.plannedTotal > 0
              ? (sec.actualTotal / sec.plannedTotal) * 100
              : 0;
          sections.push(sec);
          const bill12Uplift =
            sec.bill === 12
              ? sec.bill12ProfitTotal + sec.bill12AttendanceTotal
              : 0;
          grandPlannedBase += sec.plannedTotal;
          grandActualBase += sec.actualTotal;
          grandPlanned += sec.plannedTotal + bill12Uplift;
          grandActual += sec.actualTotal + bill12Uplift;
        }
        const grandProgress =
          grandPlannedBase > 0 ? (grandActualBase / grandPlannedBase) * 100 : 0;
        return {
          sections,
          grandPlanned,
          grandActual,
          grandProgress,
          catalog: sharedCatalog,
        };
      }

      // ── Legacy path (no projectType) ─────────────────────────────────────
      const items = legacyCatalog as ComsCatalogItem[];
      const sorted = [...items].sort((a, b) => {
        const ba = a.billNumber ?? 999;
        const bb = b.billNumber ?? 999;
        if (ba !== bb) return ba - bb;
        return (a.displayOrder ?? 0) - (b.displayOrder ?? 0);
      });

      const sectionMap = new Map<number | null, Section>();

      for (const item of sorted) {
        const bill = item.billNumber ?? null;
        if (!sectionMap.has(bill)) {
          const billEntry =
            bill != null ? BILLS.find((b) => b.number === bill) : null;
          sectionMap.set(bill, {
            bill,
            billDesc:
              billEntry?.description ?? (bill ? `Bill ${bill}` : "Unassigned"),
            items: [],
            plannedTotal: 0,
            actualTotal: 0,
            bill12ProfitTotal: 0,
            bill12AttendanceTotal: 0,
            sectionProgress: 0,
          });
        }

        const dailyQty =
          (item.id ? dailyQtyMap.get(item.id) : undefined) ??
          dailyQtyMap.get(item.name) ??
          0;

        const openingQty = item.openingActualQty ?? 0;
        const actualQty = openingQty + dailyQty;
        const plannedQty = effectivePlannedQty(
          bill,
          item.unit,
          item.plannedQty ?? 0,
          item.defaultRate ?? 0,
        );
        const rate = item.defaultRate ?? 0;
        const plannedAmt = plannedQty * rate;
        const actualAmt = actualQty * rate;
        const progress = plannedQty > 0 ? (actualQty / plannedQty) * 100 : 0;

        const sec = sectionMap.get(bill)!;
        sec.items.push({
          ...item,
          dailyQty,
          actualQty,
          plannedAmt,
          actualAmt,
          progress,
          boqEntryId: item.id, // legacy: item is its own BOQ entry
        });
        sec.plannedTotal += plannedAmt;
        sec.actualTotal += actualAmt;
        if (bill === 12) {
          sec.bill12ProfitTotal += plannedAmt * 0.05;
          sec.bill12AttendanceTotal += plannedAmt * 0.05;
        }
      }

      const sections: Section[] = [];
      let grandPlannedBase = 0,
        grandActualBase = 0,
        grandPlanned = 0,
        grandActual = 0;
      for (const sec of sectionMap.values()) {
        sec.sectionProgress =
          sec.plannedTotal > 0 ? (sec.actualTotal / sec.plannedTotal) * 100 : 0;
        sections.push(sec);
        const bill12Uplift =
          sec.bill === 12
            ? sec.bill12ProfitTotal + sec.bill12AttendanceTotal
            : 0;
        grandPlannedBase += sec.plannedTotal;
        grandActualBase += sec.actualTotal;
        grandPlanned += sec.plannedTotal + bill12Uplift;
        grandActual += sec.actualTotal + bill12Uplift;
      }
      const grandProgress =
        grandPlannedBase > 0 ? (grandActualBase / grandPlannedBase) * 100 : 0;

      return {
        sections,
        grandPlanned,
        grandActual,
        grandProgress,
        catalog: legacyCatalog,
      };
    }, [catalogueType, sharedCatalog, boqEntries, legacyCatalog, dailyQtyMap]);

  // ── Edit item save ────────────────────────────────────────────────────────
  const handleEditSave = async () => {
    if (!editItem) return;
    const plannedQty = parseFloat(editPlannedQty) || 0;
    const openingActualQty = parseFloat(editOpeningQty) || 0;
    const newName = editName.trim() || editItem.name;
    setEditSaving(true);
    setEditError("");
    try {
      if (catalogueType) {
        await uploadBaseline({
          blockId,
          overwrite: true,
          items: [
            {
              sharedItemId: editItem.sharedId,
              itemCode: editItem.itemCode || "—",
              billNumber: editItem.billNumber ?? 0,
              description: editItem.name, // shared catalogue key — don't change
              unit: editItem.unit,
              plannedQty,
              openingActualQty,
            },
          ],
        }).unwrap();
        // D5: also update the per-block entry name if changed
        if (newName !== editItem.name && editItem.boqEntryId) {
          await updateItem({ id: editItem.boqEntryId, data: { name: newName } }).unwrap();
        }
      } else {
        // Legacy path — D5: include name in update
        await updateItem({
          id: editItem.id!,
          data: { plannedQty, openingActualQty, name: newName },
        }).unwrap();
      }
      setEditItem(null);
    } catch (err: any) {
      setEditError(err?.data?.error?.message ?? "Save failed.");
    } finally {
      setEditSaving(false);
    }
  };

  // ── Mark complete toggle ──────────────────────────────────────────────────
  const handleMarkComplete = async (item: EnrichedItem) => {
    if (!item.id) return;
    const newMark = !item.markComplete;
    // Keep actual quantity factual; markComplete only affects EV logic in backend.
    const markCompleteAdjQty = 0;
    await updateItem({
      id: item.id,
      data: { markComplete: newMark, markCompleteAdjQty },
    });
  };

  // ── Baseline upload handler ───────────────────────────────────────────────
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadError("");
    setUploadResult(null);
    setNeedsOverwrite(false);
    setUploadFileName(file.name);

    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const data = new Uint8Array(ev.target!.result as ArrayBuffer);
        const wb = XLSX.read(data, { type: "array" });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows: any[][] = XLSX.utils.sheet_to_json(ws, {
          header: 1,
          defval: "",
        });

        // Parse sections separated by banner rows.
        // Banner row: non-empty col 0, all numeric cols (qty, rate, amount) are empty.
        const parsed: any[] = [];
        let currentBill = 0;

        for (const row of rows) {
          const col0 = String(row[0] ?? "").trim();
          const col3 = row[3]; // Quantity
          const col4 = row[4]; // Rate

          // Skip fully empty rows
          if (!col0 && !row[1] && !row[2]) continue;

          // Detect banner row: section name, no numeric data
          const isNumeric = (v: any) => typeof v === "number" && !isNaN(v);
          if (col0 && !isNumeric(col3) && !isNumeric(col4)) {
            // Try to match to a bill number via section name or bill description
            const upper = col0.toUpperCase();
            const billMatch = Object.entries(
              SECTION_TO_BILL as Record<string, number>,
            ).find(([k]) => upper.includes(k) || k.includes(upper.slice(0, 6)));
            if (billMatch) {
              currentBill = billMatch[1];
            }
            // Also check BILLS descriptions
            if (!billMatch) {
              const b = BILLS.find((b) =>
                upper.includes(b.description.slice(0, 8).toUpperCase()),
              );
              if (b) currentBill = b.number;
            }
            continue;
          }

          // Skip header row
          if (col0 === "Item No" || col0 === "Item Code") continue;

          const plannedQty =
            typeof col3 === "number" ? col3 : parseFloat(String(col3)) || 0;
          const openingActualQty =
            typeof row[6] === "number"
              ? row[6]
              : parseFloat(String(row[6])) || 0;

          parsed.push({
            itemCode: col0 || "—",
            billNumber: currentBill,
            description: String(row[1] ?? "").trim(),
            unit: String(row[2] ?? "").trim(),
            plannedQty,
            openingActualQty,
          });
        }

        if (parsed.length === 0) {
          setUploadError("No data rows found. Check the file format.");
          return;
        }

        setUploadItems(parsed);
      } catch (err: any) {
        setUploadError("Failed to read file: " + (err.message ?? String(err)));
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleUploadSubmit = async (overwrite: boolean) => {
    if (!uploadItems.length) return;
    setUploading(true);
    setUploadError("");
    try {
      const result = await uploadBaseline({
        blockId,
        overwrite,
        items: uploadItems,
      }).unwrap();
      setUploadResult(result);
      setNeedsOverwrite(false);
      if (!overwrite && result.unmatched.length === 0 && result.matched > 0) {
        // Check if any items were skipped due to existing non-zero actuals
        // (backend returns overwritten=0 when items exist but overwrite=false was refused)
        const skipped = result.matched - result.overwritten;
        if (skipped > 0) {
          setNeedsOverwrite(true);
        }
      }
    } catch (err: any) {
      setUploadError(err?.data?.error?.message ?? "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const resetUpload = () => {
    setUploadOpen(false);
    setUploadItems([]);
    setUploadFileName("");
    setUploadError("");
    setUploadResult(null);
    setNeedsOverwrite(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  // ── Loading ───────────────────────────────────────────────────────────────
  const isLoading = catalogueType
    ? loadingShared || loadingBoq || loadingWD
    : loadingLegacy || loadingWD;

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={8}>
        <CircularProgress />
      </Box>
    );
  }

  if (!(catalog as ComsCatalogItem[]).length) {
    return (
      <Box>
        <Typography
          variant="h2"
          sx={{ color: COMS_NAVY, fontWeight: 600, mb: 2 }}
        >
          Work Done — Progress
        </Typography>
        {catalogueType ? (
          <Typography color="text.secondary">
            No shared catalogue items found for{" "}
            <strong>{catalogueType === "type1" ? "Type 1" : "Type 2"}</strong>.
            Run <code>node _seed_catalogues.js</code> to seed the system
            catalogues.
          </Typography>
        ) : (
          <Typography color="text.secondary">
            No BOQ items found. Import a BOQ CSV from the Catalog tab first.
          </Typography>
        )}
      </Box>
    );
  }

  // ── Styling helpers ───────────────────────────────────────────────────────
  const progressColor = (pct: number) => {
    if (pct > 100) return "#c62828";
    if (pct >= 95) return "#e65100";
    return "inherit";
  };

  const hd = {
    fontWeight: 700,
    fontSize: "0.73rem",
    color: "#fff",
    bgcolor: COMS_NAVY,
    whiteSpace: "nowrap" as const,
    py: 1,
  };
  const rHd = { ...hd, textAlign: "right" as const };
  const num = { textAlign: "right" as const };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h2" sx={{ color: COMS_NAVY, fontWeight: 600 }}>
          Work Done — Progress
        </Typography>
        {catalogueType && (
          <>
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx,.xls"
              style={{ display: "none" }}
              onChange={handleFileChange}
            />
            <Button
              variant="outlined"
              startIcon={<Upload />}
              onClick={() => {
                setUploadOpen(true);
                setUploadResult(null);
                setUploadError("");
              }}
              sx={{ borderColor: COMS_NAVY, color: COMS_NAVY }}
            >
              Upload Baseline Actuals
            </Button>
          </>
        )}
      </Box>

      {/* ── Baseline upload dialog ─────────────────────────────────────── */}
      <Dialog open={uploadOpen} onClose={resetUpload} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: COMS_NAVY, color: "#fff" }}>
          Upload Baseline Actuals
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Upload a spreadsheet with columns:{" "}
            <em>
              Item No, Work Activity Description, Unit, Quantity, Rate, Amount,
              Actual Quantity, Actual Amount
            </em>
            . Sections are separated by banner rows. Each row is matched to the
            project's {catalogueType === "type1" ? "Type 1" : "Type 2"}{" "}
            catalogue by Item Code + Bill. Actual Amount is always ignored
            (computed from Rate × Qty).
          </Typography>

          {!uploadResult && (
            <Button variant="outlined" component="label" sx={{ mb: 2 }}>
              {uploadFileName || "Choose File (.xlsx / .xls)"}
              <input
                type="file"
                accept=".xlsx,.xls"
                hidden
                onChange={handleFileChange}
              />
            </Button>
          )}

          {uploadItems.length > 0 && !uploadResult && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {uploadItems.length} rows parsed from{" "}
              <strong>{uploadFileName}</strong>. Ready to import.
            </Alert>
          )}

          {uploadError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {uploadError}
            </Alert>
          )}

          {uploadResult && (
            <>
              <Alert
                severity={
                  uploadResult.unmatched.length > 0 ? "warning" : "success"
                }
                sx={{ mb: 2 }}
              >
                <strong>{uploadResult.matched} rows matched</strong> and{" "}
                {uploadResult.overwritten} per-block entries created/updated.
                {uploadResult.unmatched.length > 0 && (
                  <>
                    {" "}
                    {uploadResult.unmatched.length} rows could not be matched to
                    the catalogue.
                  </>
                )}
              </Alert>
              {uploadResult.unmatched.length > 0 && (
                <Box
                  sx={{
                    maxHeight: 200,
                    overflow: "auto",
                    bgcolor: "#fafafa",
                    p: 1,
                    borderRadius: 1,
                    fontSize: "0.75rem",
                  }}
                >
                  {uploadResult.unmatched.map((u: any, i: number) => (
                    <div key={i}>
                      Bill {u.billNumber} · [{u.itemCode}] {u.description}
                    </div>
                  ))}
                </Box>
              )}
            </>
          )}

          {needsOverwrite && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Some items already have non-zero Actual Quantities. Uploading will
              overwrite the current baseline for matched items.
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={resetUpload}>
            {uploadResult ? "Close" : "Cancel"}
          </Button>
          {!uploadResult && uploadItems.length > 0 && (
            <>
              {needsOverwrite ? (
                <Button
                  variant="contained"
                  color="warning"
                  disabled={uploading}
                  onClick={() => handleUploadSubmit(true)}
                >
                  {uploading ? "Importing…" : "Confirm Overwrite & Import"}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  disabled={uploading}
                  onClick={() => handleUploadSubmit(false)}
                  sx={{ bgcolor: COMS_NAVY, "&:hover": { bgcolor: "#1a4a7a" } }}
                >
                  {uploading ? "Importing…" : "Import"}
                </Button>
              )}
            </>
          )}
        </DialogActions>
      </Dialog>

      <TableContainer
        component={Paper}
        sx={{ borderRadius: 2, overflowX: "auto" }}
      >
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...hd, minWidth: 220 }}>Description</TableCell>
              <TableCell
                sx={{ ...hd, textAlign: "center" as const, width: 64 }}
              >
                Unit
              </TableCell>
              <TableCell sx={{ ...rHd, width: 90 }}>Planned Qty</TableCell>
              <TableCell sx={{ ...rHd, width: 90 }}>Rate</TableCell>
              <TableCell sx={{ ...rHd, width: 110 }}>Planned Amt</TableCell>
              <TableCell sx={{ ...rHd, width: 90 }}>Actual Qty</TableCell>
              <TableCell sx={{ ...rHd, width: 110 }}>Actual Amt</TableCell>
              <TableCell sx={{ ...rHd, width: 90 }}>% Progress</TableCell>
              <TableCell
                sx={{ ...hd, textAlign: "center" as const, width: 80 }}
              >
                Complete
              </TableCell>
              <TableCell sx={{ ...hd, width: 48 }} />
            </TableRow>
          </TableHead>

          <TableBody>
            {sections.map((sec) => (
              <React.Fragment key={sec.bill ?? "unassigned"}>
                {/* ── Section header ── */}
                <TableRow>
                  <TableCell
                    colSpan={10}
                    sx={{
                      fontWeight: 800,
                      fontSize: "0.78rem",
                      color: COMS_NAVY,
                      bgcolor: "#e8edf5",
                      borderLeft: `4px solid ${COMS_NAVY}`,
                      py: 1,
                      letterSpacing: 0.5,
                    }}
                  >
                    {sec.bill != null ? `Bill ${sec.bill} — ` : ""}
                    {sec.billDesc}
                  </TableCell>
                </TableRow>

                {/* ── Item rows ── */}
                {sec.items.map((item) => (
                  <React.Fragment key={item.id ?? item.name}>
                    <TableRow
                      sx={{ "&:hover": { bgcolor: "rgba(18,52,86,0.04)" } }}
                    >
                      <TableCell sx={{ fontSize: "0.78rem", py: 0.75, pl: 3 }}>
                        {item.name}
                      </TableCell>
                      <TableCell
                        sx={{ fontSize: "0.78rem", textAlign: "center" as const }}
                      >
                        {item.unit}
                      </TableCell>
                      <TableCell sx={{ ...num, fontSize: "0.78rem" }}>
                        {fmtQty(item.plannedQty ?? 0)}
                      </TableCell>
                      <TableCell sx={{ ...num, fontSize: "0.78rem" }}>
                        {fmt(item.defaultRate ?? 0)}
                      </TableCell>
                      <TableCell sx={{ ...num, fontSize: "0.78rem" }}>
                        {fmt(item.plannedAmt)}
                      </TableCell>
                      <TableCell sx={{ ...num, fontSize: "0.78rem" }}>
                        {fmtQty(item.actualQty)}
                      </TableCell>
                      <TableCell sx={{ ...num, fontSize: "0.78rem" }}>
                        {fmt(item.actualAmt)}
                      </TableCell>
                      <TableCell
                        sx={{
                          ...num,
                          fontSize: "0.78rem",
                          fontWeight: item.progress > 100 ? 700 : 400,
                          color: progressColor(item.progress),
                        }}
                      >
                        {item.progress.toFixed(0)}%
                      </TableCell>
                      <TableCell sx={{ textAlign: "center" as const, p: 0 }}>
                        <Tooltip
                          title={
                            item.markComplete
                              ? "Unmark complete — EV returns to measured progress for this item"
                              : "Mark complete — EV takes full planned amount while actual quantity remains as recorded"
                          }
                        >
                          <Checkbox
                            size="small"
                            checked={!!item.markComplete}
                            onChange={() => handleMarkComplete(item)}
                            sx={{
                              p: 0.5,
                              color: COMS_NAVY,
                              "&.Mui-checked": { color: "#2e7d32" },
                            }}
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ p: 0, textAlign: "center" as const }}>
                        <Tooltip title="Edit planned qty &amp; baseline actual qty">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setEditItem(item);
                              setEditName(item.name);
                              setEditPlannedQty(String(item.plannedQty ?? 0));
                              setEditOpeningQty(
                                String(item.openingActualQty ?? 0),
                              );
                              setEditError("");
                            }}
                            sx={{
                              color: COMS_NAVY,
                              opacity: 0.6,
                              "&:hover": { opacity: 1 },
                            }}
                          >
                            <Edit fontSize="inherit" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>

                    {sec.bill === 12 && (
                      <TableRow sx={{ bgcolor: "#fafbff" }}>
                        <TableCell
                          colSpan={10}
                          sx={{ fontSize: "0.74rem", py: 0.6, pl: 5, color: "#5a5f73" }}
                        >
                          Bill 12 uplift: Profit 5% {fmt(item.plannedAmt * 0.05)} · Attendance 5% {fmt(item.plannedAmt * 0.05)}
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}

                {/* ── Section subtotal ── */}
                <TableRow
                  sx={{ bgcolor: "#f0f4f8", borderTop: "2px solid #c5d3e8" }}
                >
                  <TableCell
                    colSpan={4}
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.78rem",
                      color: "#555",
                      pl: 3,
                      py: 1,
                    }}
                  >
                    {sec.bill != null ? `Bill ${sec.bill}` : "Subtotal"} —
                    subtotal
                  </TableCell>
                  <TableCell
                    sx={{
                      ...num,
                      fontWeight: 700,
                      fontSize: "0.8rem",
                      color: COMS_NAVY,
                    }}
                  >
                    {fmt(
                      sec.plannedTotal +
                        (sec.bill === 12
                          ? sec.bill12ProfitTotal + sec.bill12AttendanceTotal
                          : 0),
                    )}
                  </TableCell>
                  <TableCell />
                  <TableCell
                    sx={{
                      ...num,
                      fontWeight: 700,
                      fontSize: "0.8rem",
                      color: COMS_NAVY,
                    }}
                  >
                    {fmt(
                      sec.actualTotal +
                        (sec.bill === 12
                          ? sec.bill12ProfitTotal + sec.bill12AttendanceTotal
                          : 0),
                    )}
                  </TableCell>
                  <TableCell
                    sx={{
                      ...num,
                      fontWeight: 700,
                      fontSize: "0.8rem",
                      color: progressColor(sec.sectionProgress),
                    }}
                  >
                    {sec.sectionProgress.toFixed(0)}%
                  </TableCell>
                  <TableCell />
                  <TableCell />
                </TableRow>

                {sec.bill === 12 && (sec.bill12ProfitTotal > 0 || sec.bill12AttendanceTotal > 0) && (
                  <TableRow sx={{ bgcolor: "#eef3ff" }}>
                    <TableCell
                      colSpan={10}
                      sx={{ fontWeight: 700, fontSize: "0.76rem", color: "#3949ab", pl: 3 }}
                    >
                      Included in subtotal: Profit 5% {fmt(sec.bill12ProfitTotal)} + Attendance 5% {fmt(sec.bill12AttendanceTotal)} = {fmt(sec.bill12ProfitTotal + sec.bill12AttendanceTotal)}
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            ))}

            {/* ── Grand total ── */}
            <TableRow sx={{ bgcolor: COMS_NAVY }}>
              <TableCell
                colSpan={4}
                sx={{
                  fontWeight: 800,
                  color: "#fff",
                  fontSize: "0.85rem",
                  py: 1.5,
                }}
              >
                GRAND TOTAL — Measured Works
              </TableCell>
              <TableCell
                sx={{
                  ...num,
                  fontWeight: 800,
                  color: "#fff",
                  fontSize: "0.85rem",
                }}
              >
                {fmt(grandPlanned)}
              </TableCell>
              <TableCell />
              <TableCell
                sx={{
                  ...num,
                  fontWeight: 800,
                  color: "#fff",
                  fontSize: "0.85rem",
                }}
              >
                {fmt(grandActual)}
              </TableCell>
              <TableCell
                sx={{
                  ...num,
                  fontWeight: 800,
                  color: "#fff",
                  fontSize: "0.85rem",
                }}
              >
                {grandProgress.toFixed(0)}%
              </TableCell>
              <TableCell />
              <TableCell />
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* ── Edit item dialog ────────────────────────────────────────────── */}
      <Dialog
        open={!!editItem}
        onClose={() => !editSaving && setEditItem(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: COMS_NAVY, color: "#fff" }}>
          Edit Item
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {/* D5: allow editing description */}
          <TextField
            label="Description"
            fullWidth
            sx={{ mb: 2 }}
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            disabled={editSaving}
            helperText="Edit the activity description/name"
          />
          <TextField
            label="Planned Qty"
            type="number"
            fullWidth
            sx={{ mb: 2 }}
            inputProps={{ min: 0, step: "0.01" }}
            value={editPlannedQty}
            onChange={(e) => setEditPlannedQty(e.target.value)}
            disabled={editSaving}
          />
          <TextField
            label="Baseline Actual Qty (opening balance)"
            type="number"
            fullWidth
            sx={{ mb: 1 }}
            inputProps={{ min: 0, step: "0.01" }}
            value={editOpeningQty}
            onChange={(e) => setEditOpeningQty(e.target.value)}
            disabled={editSaving}
            helperText="Work completed prior to go-live. Added to daily logged quantities."
          />
          {editError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {editError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditItem(null)} disabled={editSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={editSaving}
            onClick={handleEditSave}
            sx={{ bgcolor: COMS_NAVY, "&:hover": { bgcolor: "#1a4a7a" } }}
          >
            {editSaving ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WorkDone;
